import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError, type Device, type DeviceGroup } from '../lib/api'
import { fmtAgo } from '../lib/format'
import { useAuth } from '../lib/auth'
import { Card, Empty, Field, HealthPill, Loading, Modal } from '../components/ui'

const COLOURS = ['#3EB6A0', '#709ED6', '#E0A84E', '#E86A60', '#A78BFA', '#96ABA5']

export default function DeviceGroups() {
  const qc = useQueryClient()
  const { can } = useAuth()
  const [editing, setEditing] = useState<DeviceGroup | 'new' | null>(null)

  const groups = useQuery({
    queryKey: ['device-groups'],
    queryFn: () => api.get<DeviceGroup[]>('/api/admin/device-groups'),
  })

  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/api/admin/device-groups/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['device-groups'] }),
  })

  return (
    <div className="stack" style={{ gap: 18 }}>
      <div className="page-head">
        <div>
          <h1>Device groups</h1>
          <div className="page-sub">
            Named pools of devices that sources route through. Adding a SIM to a pool is one edit here rather than
            one edit per source. A source with no groups uses the whole approved fleet.
          </div>
        </div>
        {can('Admin') && (
          <button className="btn primary" onClick={() => setEditing('new')}>
            New group
          </button>
        )}
      </div>

      {groups.isLoading ? (
        <Loading />
      ) : groups.data?.length === 0 ? (
        <Card>
          <Empty
            title="No groups yet"
            hint="Until you create one, every source can use every approved device. That is a fine default for a single-tenant fleet."
          />
        </Card>
      ) : (
        <div className="grid cols-2">
          {groups.data?.map((g) => (
            <Card
              key={g.id}
              title={
                <span className="row" style={{ gap: 8 }}>
                  <i
                    className="dot"
                    style={{ background: g.colour ?? 'rgb(var(--glow))', width: 8, height: 8 }}
                  />
                  {g.name}
                  {!g.isActive && <span className="chip">inactive</span>}
                </span>
              }
              actions={
                can('Admin') && (
                  <div className="btn-row">
                    <button className="btn sm" onClick={() => setEditing(g)}>
                      Edit
                    </button>
                    <button
                      className="btn danger sm"
                      onClick={() => remove.mutate(g.id)}
                      title={g.sources.length > 0 ? 'Detach it from its sources first' : 'Delete group'}
                    >
                      Delete
                    </button>
                  </div>
                )
              }
            >
              {g.description && <p className="muted small" style={{ marginTop: 0 }}>{g.description}</p>}

              <div className="field-label">Devices ({g.members.length})</div>
              <div className="row wrap" style={{ gap: 5, marginBottom: 14 }}>
                {g.members.length === 0 ? (
                  <span className="faint small">
                    Empty — this group contributes nothing to a source's pool.
                  </span>
                ) : (
                  g.members.map((m) => (
                    <span key={m.simClientId} className="chip">
                      {m.name}
                    </span>
                  ))
                )}
              </div>

              <div className="field-label">Used by</div>
              <div className="row wrap" style={{ gap: 5 }}>
                {g.sources.length === 0 ? (
                  <span className="faint small">No source routes through this group yet.</span>
                ) : (
                  g.sources.map((s) => (
                    <span key={s.id} className="chip">
                      {s.name}
                    </span>
                  ))
                )}
              </div>

              <div className="faint small" style={{ marginTop: 12 }}>
                updated {fmtAgo(g.updatedAt)}
              </div>
            </Card>
          ))}
        </div>
      )}

      {remove.error && <div className="banner danger">{(remove.error as ApiError).message}</div>}

      {editing && (
        <GroupModal
          group={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onDone={() => {
            setEditing(null)
            void qc.invalidateQueries({ queryKey: ['device-groups'] })
            void qc.invalidateQueries({ queryKey: ['devices'] })
          }}
        />
      )}
    </div>
  )
}

function GroupModal({
  group,
  onClose,
  onDone,
}: {
  group: DeviceGroup | null
  onClose: () => void
  onDone: () => void
}) {
  const [name, setName] = useState(group?.name ?? '')
  const [description, setDescription] = useState(group?.description ?? '')
  const [colour, setColour] = useState(group?.colour ?? COLOURS[0])
  const [isActive, setIsActive] = useState(group?.isActive ?? true)
  const [members, setMembers] = useState<string[]>(group?.members.map((m) => m.simClientId) ?? [])
  const [error, setError] = useState<string | null>(null)

  const devices = useQuery({
    queryKey: ['devices'],
    queryFn: () => api.get<Device[]>('/api/admin/devices'),
  })

  const save = useMutation({
    mutationFn: () => {
      const body = { name, description: description || null, colour, isActive, clientIds: members }
      return group ? api.put(`/api/admin/device-groups/${group.id}`, body) : api.post('/api/admin/device-groups', body)
    },
    onSuccess: onDone,
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Could not save the group.'),
  })

  const toggle = (id: string) =>
    setMembers((current) => (current.includes(id) ? current.filter((x) => x !== id) : [...current, id]))

  return (
    <Modal
      title={group ? `Edit ${group.name}` : 'New device group'}
      onClose={onClose}
      wide
      footer={
        <>
          <button className="btn ghost" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" onClick={() => save.mutate()} disabled={!name.trim() || save.isPending}>
            {save.isPending ? 'Saving…' : 'Save group'}
          </button>
        </>
      }
    >
      {error && <div className="banner danger">{error}</div>}

      <div className="grid cols-2" style={{ gap: 16 }}>
        <Field label="Name">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Emtel pool" autoFocus />
        </Field>
        <Field label="Colour" hint="Used to recognise the pool at a glance elsewhere.">
          <div className="row" style={{ gap: 7 }}>
            {COLOURS.map((c) => (
              <button
                key={c}
                onClick={() => setColour(c)}
                aria-label={c}
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: 8,
                  background: c,
                  border: colour === c ? '2px solid rgb(var(--ink))' : '1px solid rgb(var(--line))',
                  cursor: 'pointer',
                }}
              />
            ))}
          </div>
        </Field>
      </div>

      <Field label="Description" hint="Optional. What this pool is for.">
        <input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Primary OTP fleet — Emtel SIMs only"
        />
      </Field>

      <label className="check" style={{ marginBottom: 16 }}>
        <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
        Active
      </label>
      {!isActive && (
        <div className="banner warn">
          <div>
            An inactive group contributes no devices. Any source that routes only through this group falls back to
            the whole approved fleet.
          </div>
        </div>
      )}

      <div className="field-label">Members ({members.length})</div>
      <div
        style={{
          maxHeight: 280,
          overflow: 'auto',
          border: '1px solid rgb(var(--line))',
          borderRadius: 'var(--radius-sm)',
        }}
      >
        <table>
          <tbody>
            {devices.data?.map((d) => (
              <tr key={d.id}>
                <td style={{ width: 36 }}>
                  <input type="checkbox" checked={members.includes(d.id)} onChange={() => toggle(d.id)} />
                </td>
                <td>
                  <strong>{d.name}</strong>
                  <div className="faint small mono">{d.msisdn ?? d.deviceId}</div>
                </td>
                <td>
                  <HealthPill state={d.healthState} reason={d.darkReason} />
                </td>
                <td className="faint small">{d.operator ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Modal>
  )
}
